import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ClientsV2Service,
  ExternalUsersService,
} from '@wilson/clients/services';
import { flatten } from 'lodash';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { BaseSelectFilterDirective } from '../../base-select-filter.directive';
import { FilterService } from '../../filter.service';
import { FilterOptions } from '../../options.interface';
import { MultiSelectParserService } from '../../services/multi-select-parser.service';
import { ClientPartnership } from '@wilson/interfaces';

@Component({
  selector: 'wilson-shared-user-filter',
  imports: [CommonModule, NzSelectModule, TranslatePipe, FormsModule],
  templateUrl: './shared-user-filter.component.html',
  styleUrl: './shared-user-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedUserFilterComponent extends BaseSelectFilterDirective<
  FilterOptions,
  string[]
> {
  protected readonly filterService = inject(FilterService);
  protected readonly parserService = inject(MultiSelectParserService);
  protected readonly clientsV2Service = inject(ClientsV2Service);
  protected readonly externalUsersService = inject(ExternalUsersService);

  @Input() showLabel = false;
  @Input() placeholder!: string;

  constructor() {
    super('sharedUsers');
  }

  protected initializeOptions(): Observable<FilterOptions[]> {
    return this.clientsV2Service
      .findAll({
        relations: ['clientPartnership'],
        limit: 0,
      })
      .pipe(
        map((clients) => {
          return clients.data
            .filter(
              (client) =>
                client.id !== undefined &&
                client.clientPartnership &&
                client.clientPartnership.partnershipId,
            )
            .map(
              (client) =>
                (client.clientPartnership as ClientPartnership).partnershipId,
            );
        }),
        switchMap((partnershipIds) =>
          forkJoin(
            partnershipIds.map((id) => this.externalUsersService.getUser(id)),
          ),
        ),
        map((usersPerPartnership) => flatten(usersPerPartnership)),
        map((externalUsers) => {
          const filterOptionUsers: FilterOptions[] = externalUsers.map(
            (externalUser) => {
              return {
                id: externalUser.userId as string,
                name:
                  externalUser.user.firstName +
                  ' ' +
                  externalUser.user.lastName,
              };
            },
          );
          filterOptionUsers.sort((a, b) => a.name.localeCompare(b.name));
          return filterOptionUsers;
        }),
        catchError(() => of([])),
      );
  }
}
